if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this._priceEl = this.querySelector('[data-sticky-price]');
        this._submitBtn = this.querySelector('[data-sticky-submit]');
        this._bisEl = this.querySelector('[data-sticky-bis]');

        // Native ATC button (the one we observe for visibility)
        this._nativeBtn = document.querySelector('[id^="ProductSubmitButton-"]');
        this._qtyInput = document.querySelector('product-info .quantity__input');

        this._setupVisibility();
        this._setupVariantSync();
        this._setupQtySync();
        this._setupSubmit();
        this._setupBisObserver();

        // Set initial state from Liquid-rendered values
        this._price = parseInt(this.dataset.initialPrice, 10) || 0;
        this._qty = this._qtyInput ? Math.max(1, parseInt(this._qtyInput.value, 10) || 1) : 1;
        this._available = this.dataset.initialAvailable === 'true';
        this._updateDisplay();
      }

      disconnectedCallback() {
        this._observer?.disconnect();
        this._variantUnsub?.();
        this._bisObserver?.disconnect();
        if (this._qtyInput && this._onQtyChange) {
          this._qtyInput.removeEventListener('change', this._onQtyChange);
        }
      }

      // ── Visibility ──────────────────────────────────────────────────────────

      _setupVisibility() {
        if (!this._nativeBtn) return;

        this._observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this._setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          },
          { threshold: 0 }
        );
        this._observer.observe(this._nativeBtn);
      }

      _setVisible(show) {
        if (show === this._isVisible) return;
        this._isVisible = show;

        this.classList.remove('animation-done');
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');

        // Remove will-change after transition to free GPU memory
        this.addEventListener(
          'transitionend',
          () => this.classList.add('animation-done'),
          { once: true }
        );
      }

      // ── Variant sync ────────────────────────────────────────────────────────

      _setupVariantSync() {
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const variant = data?.variant;
            if (!variant) return;
            this._price = variant.price || 0;
            this._available = variant.available;
            // Re-read qty: Dawn resets it to min via setQuantityBoundries before publishing
            if (this._qtyInput) {
              this._qty = Math.max(1, parseInt(this._qtyInput.value, 10) || 1);
            }
            this._updateDisplay();
          });
        }
      }

      // ── Qty sync ────────────────────────────────────────────────────────────

      _setupQtySync() {
        if (!this._qtyInput) return;
        this._onQtyChange = () => {
          this._qty = Math.max(1, parseInt(this._qtyInput.value, 10) || 1);
          this._updateDisplay();
        };
        this._qtyInput.addEventListener('change', this._onQtyChange);
      }

      // ── BIS observer — wait for Klaviyo to inject its button ────────────────

      _setupBisObserver() {
        const existing = document.querySelector('#klaviyo-bis-button-container button');
        if (existing) {
          this._klaviyoBisBtn = existing;
          return;
        }
        this._bisObserver = new MutationObserver(() => {
          const btn = document.querySelector('#klaviyo-bis-button-container button');
          if (btn) {
            this._klaviyoBisBtn = btn;
            this._bisObserver.disconnect();
          }
        });
        this._bisObserver.observe(document.body, { childList: true, subtree: true });
      }

      // ── Submit ──────────────────────────────────────────────────────────────

      _setupSubmit() {
        this._submitBtn?.addEventListener('click', () => {
          if (!this._available || this._submitBtn.disabled) return;
          // Delegate to the native button — it owns the form, quantity, and all listeners
          this._nativeBtn?.click();
        });
        this._bisEl?.addEventListener('click', () => {
          this._klaviyoBisBtn?.click();
        });
      }

      // ── Display ─────────────────────────────────────────────────────────────

      _updateDisplay() {
        if (this._available) {
          this._submitBtn?.removeAttribute('hidden');
          this._bisEl?.setAttribute('hidden', '');
          if (this._priceEl) {
            this._priceEl.textContent = this._formatMoney(this._price * (this._qty || 1));
          }
        } else {
          this._submitBtn?.setAttribute('hidden', '');
          this._bisEl?.removeAttribute('hidden');
        }
        if (this._submitBtn) this._submitBtn.disabled = !this._available;
      }

      // ── Money formatting ────────────────────────────────────────────────────

      _formatMoney(cents) {
        if (typeof cents === 'string') cents = cents.replace('.', '');

        const format = this.dataset.moneyFormat || '${{amount}}';
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return format;

        const _fmt = (num, precision, thousands, decimal) => {
          precision = precision ?? 2;
          thousands = thousands ?? ',';
          decimal = decimal ?? '.';
          if (isNaN(num) || num == null) return '0';
          num = (num / 100.0).toFixed(precision);
          const parts = num.split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return whole + (parts[1] ? decimal + parts[1] : '');
        };

        let value = '';
        switch (match[1]) {
          case 'amount':                            value = _fmt(cents, 2); break;
          case 'amount_no_decimals':                value = _fmt(cents, 0); break;
          case 'amount_with_comma_separator':       value = _fmt(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = _fmt(cents, 0, '.', ','); break;
          default:                                  value = _fmt(cents, 2);
        }

        return format.replace(/\{\{\s*\w+\s*\}\}/, value);
      }
    }
  );
}
